import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import Screen from '../../components/screen';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  checkUserAuthenticated, convertBoolenToString, formatDateForDataGrid, getReplacementOrderList,
  goToRoutePage, setWindowTitle
} from '../../utils/helper';
import {
  Box, Breadcrumbs, Button, FormControl, Grid, InputLabel, LinearProgress, Link, Select,
  TextField, Typography
} from '../../utils/material-ui-core';

interface OrderType {
  id: number;
  name: string;
  showProcessDate: boolean;
}

const initialOrderTypes: OrderType[] = [
  { id: 0, name: "Queued Orders", showProcessDate: false },
  { id: 1, name: "Unprocessed Orders", showProcessDate: false },
  { id: 2, name: "Completed Orders", showProcessDate: true },
  { id: 3, name: "Failed Orders", showProcessDate: true },
];

const moment = require("moment");

const Replacement = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, setReplacementOrders, replacementOrders, showAlert } = useContext(AppContext);

  const replacementSearchCriteriaStorageName = "ReplacementSearchCriteria";

  const [loading, setLoading] = useState<boolean>(true);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>(initialOrderTypes);
  const [orderTypeId, setOrderTypId] = useState<number>(-1);
  const [processDate, setProcessDate] = useState<Date>(moment("05/13/2021").format("YYYY-MM-DD"));
  const [visibleProcessDate, setVisibleProcessDate] = useState<boolean>(false);

  const columns = [
    { field: "companyCode", headerName: "Company Code", width: 200 },
    { field: "programCode", headerName: "Program Code", width: 200 },
    { field: "accountNumber", headerName: "Account #", width: 200, hide: true },
    { field: "accountName", headerName: "Account Name", width: 300 },
    {
      field: "isValidationSuccess",
      headerName: "Status",
      width: 120,
      valueFormatter: (params: GridValueFormatterParams) => convertBoolenToString(params.value as boolean),
    },
    {
      field: "organizationName",
      headerName: "Ship To Organization Name",
      width: 300,
    },
    { field: "firstName", headerName: "First Name", width: 300, hide: true },
    { field: "lastName", headerName: "Last Name", width: 300, hide: true },
    { field: "email", headerName: "Email", width: 300, hide: true },
    { field: "phone", headerName: "Phone", width: 300, hide: true },
    {
      field: "addressline1",
      headerName: "Address Line 1",
      width: 200,
      hide: true,
    },
    {
      field: "addressline2",
      headerName: "Address Line 2",
      width: 200,
      hide: true,
    },
    { field: "locality1", headerName: "Locality 1", width: 150, hide: true },
    { field: "region1", headerName: "Region 1", width: 150, hide: true },
    { field: "postalCode", headerName: "Postal Code", width: 150, hide: true },
    { field: "country", headerName: "Country", width: 150, hide: true },
    {
      field: "dqPass",
      headerName: "DQ Passed",
      width: 150,
      valueFormatter: (params: GridValueFormatterParams) => convertBoolenToString(params.value as boolean),
    },
    {
      field: "kitNo",
      headerName: "Kit No",
      width: 150,
      type: "number",
      hide: true,
    },
    {
      field: "installOption",
      headerName: "Install Option",
      width: 200,
      hide: true,
    },
    {
      field: "replacementOrderShortOrderId",
      headerName: "Order #",
      width: 150,
    },
    {
      field: "replacementOrderCompletedDate",
      headerName: "Order Completed Date",
      width: 250,
      type: "dateTime",
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    {
      field: "isValidationSuccess",
      headerName: "Validation Passed",
      width: 150,
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => convertBoolenToString(params.value as boolean),
    },
    {
      field: "shippingAddressAutoCorrectedByDQ",
      headerName: "Auto Corrected By DQ",
      width: 250,
      hide: true,
      valueFormatter: (params: GridValueFormatterParams) => convertBoolenToString(params.value as boolean),
    },
    {
      field: "modifiedDate",
      headerName: "Process Date",
      width: 200,
      type: "dateTime",
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
  ];

  const searchCriteriaChanged = () => {
    setReplacementOrders([]);
  };

  const saveFilterCriteria = () => {
    const filterInput = {
      orderTypeId,
      processDate,
    };
    localStorage.setItem(replacementSearchCriteriaStorageName, JSON.stringify(filterInput));
  };

  const orderTypeChanged = (idx: number) => {
    const showProcessDateConfirm = orderTypes.find((y) => {
      return y.id === idx;
    })?.showProcessDate;
    if (showProcessDateConfirm !== undefined && showProcessDateConfirm) {
      setVisibleProcessDate(true);
      return;
    }
    setVisibleProcessDate(false);
  };

  const handleDataGridClick = (params: GridRowParams, event: any) => { };

  const getData = () => {
    const rows: any = [];
    replacementOrders.map((replacementOrder, index) =>
      rows.push({
        id: index,
        ...replacementOrder,
        ...replacementOrder.shipping,
        ...replacementOrder.shipping.address,
      })
    );
    return rows;
  };

  const search = async () => {
    setLoading(true);
    saveFilterCriteria();
    setReplacementOrders([]);
    const apiResponse = await getReplacementOrderList(orderTypeId, processDate);
    if (apiResponse.success) {
      const data = apiResponse.data;
      setReplacementOrders(data.replacementOrders as ReplacementOrder[]);
      getData();
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }
    setLoading(false);
  };

  useEffect(() => {
    setWindowTitle("Replacement Order");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        setOrderTypes(initialOrderTypes);

        try {
          const searchCriteriaString = localStorage.getItem(replacementSearchCriteriaStorageName);
          if (searchCriteriaString !== null) {
            const searchCriteria = JSON.parse(searchCriteriaString);
            setOrderTypId(searchCriteria.orderTypeId);
            setProcessDate(moment(searchCriteria.processDate).format("YYYY-MM-DD"));
            orderTypeChanged(searchCriteria.orderTypeId);

            if (replacementOrders.length > 0) {
              getData();
            }
          }
        } catch (e) {
          if (initialOrderTypes.length > 0) {
            setOrderTypId(initialOrderTypes[0].id);
            orderTypeChanged(initialOrderTypes[0].id);
          }
        }
        setLoading(false);
      }
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <React.Fragment>
      <Screen>
        <Box className={pageStyles.root}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box className={globalStyles.bottomSpacer}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                    Home
                  </Link>
                  <Typography color="textPrimary">Replacement</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={6} sm={8} md={3} lg={2} xl={2}>
              <FormControl className={globalStyles.formControl} fullWidth={true}>
                <InputLabel shrink={true}>Order Type</InputLabel>
                <Select
                  required
                  native
                  labelId="order-type-id-label"
                  id="orderTypeId"
                  name="orderTypeId"
                  value={orderTypeId}
                  onChange={(e) => {
                    if (e !== null) {
                      setOrderTypId(Number(e.target.value));
                      orderTypeChanged(Number(e.target.value));
                    }
                    searchCriteriaChanged();
                  }}
                  label="Order Type"
                >
                  {orderTypes.map((orderType, index) => (
                    <option value={orderType.id} key={index}>
                      {orderType.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {visibleProcessDate ? (
              <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
                <TextField
                  required
                  type="date"
                  id="processDate"
                  name="processDate"
                  label="processDate"
                  value={processDate}
                  onChange={(e) => {
                    if (e !== null) {
                      setProcessDate(moment(e.target.value).format("YYYY-MM-DD"));
                    }
                    searchCriteriaChanged();
                  }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Box p={3} pt={3} display="flex" justifyContent="center">
                <Button id="button-search" variant="contained" color="secondary" className={globalStyles.buttonDefault} disabled={loading} onClick={() => search()}>
                  {loading ? "Searching..." : "Search"}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box className={pageStyles.datagridContainer}>
                {loading ? <LinearProgress color="secondary" /> : null}
                <CustomDataGrid uniqueGridCode={"ReplacementResult"} columns={columns} rows={getData()} showToolBar={true} sortModel={[{ field: "accountName", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Screen>
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      minHeight: "350px",
      height: "calc(100vh - 500px)",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
  })
);

export default Replacement;
